<template>
  <AlertDialog v-model:open="isOpen">
    <AlertDialogTrigger as-child>
      <Button
        variant="outline"
        class="h-11 transition-all hover:border-primary hover:text-primary"
        :disabled="loading"
      >
        <div class="flex items-center justify-center gap-2 text-sm font-semibold">
          <BellOff v-if="subscribed" class="h-4 w-4" />
          <Bell v-else class="h-4 w-4" />
          <SfProgressCircular v-if="loading" size="sm" />
          <span>{{ buttonText }}</span>
        </div>
      </Button>
    </AlertDialogTrigger>
    
    <AlertDialogContent class="max-w-lg">
      <AlertDialogHeader>
        <div class="flex items-start gap-4">
          <div class="shrink-0 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Bell class="h-6 w-6 text-primary" />
          </div>
          <div class="flex-1">
            <AlertDialogTitle class="text-xl font-semibold">
              {{ subscribed ? t('product_availability.modal_title_manage') : t('product_availability.modal_title_subscribe') }}
            </AlertDialogTitle>
            <AlertDialogDescription class="mt-2 text-sm text-muted-foreground">
              {{ t('product_availability.modal_description') }}
            </AlertDialogDescription>
          </div>
        </div>
      </AlertDialogHeader>

      <div class="space-y-4 py-4">
        <!-- Subscription Status Card -->
        <div v-if="subscribed && !showUnsubscribeConfirm" class="rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/30 p-4">
          <div class="flex items-start gap-3">
            <CheckCircle class="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
            <div class="flex-1">
              <h4 class="font-medium text-green-900 dark:text-green-100">
                {{ t('product_availability.already_subscribed_title') }}
              </h4>
              <p class="text-sm text-green-700 dark:text-green-300 mt-1">
                {{ t('product_availability.already_subscribed_description', { email: customerEmail }) }}
              </p>
            </div>
          </div>
        </div>

        <!-- Unsubscribe Confirmation Card -->
        <div v-if="showUnsubscribeConfirm" class="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-4">
          <div class="flex items-start gap-3">
            <AlertCircle class="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div class="flex-1">
              <h4 class="font-medium text-amber-900 dark:text-amber-100">
                {{ t('product_availability.unsubscribe_confirm_title') }}
              </h4>
              <p class="text-sm text-amber-700 dark:text-amber-300 mt-1">
                {{ t('product_availability.unsubscribe_confirm_description') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Email Form -->
        <form v-if="!subscribed || !isAuthenticated" @submit.prevent="handleSubmit" class="space-y-4">
          <div class="space-y-2">
            <Label for="notify-email" class="text-sm font-medium">
              {{ t('product_availability.email_label') }} <span class="text-red-500">*</span>
            </Label>
            <div class="relative">
              <Input
                id="notify-email"
                v-model="customerEmail"
                type="email"
                :placeholder="t('product_availability.email_placeholder')"
                :class="{
                  'border-red-500 focus-visible:ring-red-500': validationErrors.email,
                  'pl-10': true
                }"
                :disabled="isSubmitting || isAuthenticated"
                required
                class="h-11"
              />
              <Mail class="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
            </div>
            <p v-if="validationErrors.email" class="text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertCircle class="h-4 w-4" />
              {{ validationErrors.email }}
            </p>
          </div>

          <!-- Terms Acceptance -->
          <div class="rounded-lg border bg-muted/50 p-4">
            <div class="flex items-start gap-3">
              <Checkbox
                id="notify-terms"
                v-model:model-value="acceptTerms"
                :class="{
                  'border-red-500': validationErrors.acceptTerms,
                }"
                :disabled="isSubmitting"
                class="mt-1"
              />
              <div class="flex-1">
                <Label for="notify-terms" class="text-sm font-normal leading-relaxed cursor-pointer">
                  {{ t('product_availability.terms_label') }}
                </Label>
                <p v-if="validationErrors.acceptTerms" class="text-sm text-red-600 dark:text-red-400 mt-2 flex items-center gap-1">
                  <AlertCircle class="h-4 w-4" />
                  {{ validationErrors.acceptTerms }}
                </p>
              </div>
            </div>
          </div>
        </form>
      </div>
      
      <AlertDialogFooter class="gap-2 sm:gap-2">
        <!-- Subscribed State Actions -->
        <template v-if="subscribed && !showUnsubscribeConfirm">
          <AlertDialogCancel class="flex-1">
            {{ t('product_availability.button_close') }}
          </AlertDialogCancel>
          <Button
            @click="showUnsubscribeConfirm = true"
            variant="outline"
            :disabled="isSubmitting"
            class="flex-1 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <BellOff class="mr-2 h-4 w-4" />
            {{ t('product_availability.button_unsubscribe') }}
          </Button>
        </template>

        <!-- Unsubscribe Confirmation Actions -->
        <template v-else-if="showUnsubscribeConfirm">
          <AlertDialogCancel @click="showUnsubscribeConfirm = false" :disabled="isSubmitting" class="flex-1">
            {{ t('product_availability.button_cancel') }}
          </AlertDialogCancel>
          <Button
            @click="handleUnsubscribe"
            variant="destructive"
            :disabled="isSubmitting"
            class="flex-1 text-white"
          >
            <SfProgressCircular v-show="isSubmitting" class="mr-2!" size="sm" />
            {{ t('product_availability.button_confirm_unsubscribe') }}
          </Button>
        </template>

        <!-- Subscribe Actions -->
        <template v-else>
          <AlertDialogCancel :disabled="isSubmitting" class="flex-1">
            {{ t('product_availability.button_cancel') }}
          </AlertDialogCancel>
          <Button
            @click="handleSubmit"
            :disabled="!acceptTerms || isSubmitting || (customerEmail && !validateEmail(customerEmail))"
            class="flex-1"
          >
            <SfProgressCircular v-show="isSubmitting" class="mr-2!" size="sm" />
            <Bell v-show="!isSubmitting" class="mr-2 h-4 w-4" />
            {{ t('product_availability.button_submit') }}
          </Button>
        </template>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed, onMounted } from 'vue';
import { Bell, BellOff, CheckCircle, AlertCircle, Mail } from 'lucide-vue-next';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Checkbox } from '~/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { SfProgressCircular } from '@storefront-ui/vue';
import { useProductAvailabilityNotification } from '~/composables/useProductAvailabilityNotification';
import { useCustomer } from '~/composables/useCustomer';
import useNotifications from '~/stores/notifications';

interface Props {
  productId: number;
  productAttributeId?: number;
  productName?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'subscribed': [email: string];
  'unsubscribed': [email: string];
}>();

// Composables
const { t } = useI18n();
const { subscribe, unsubscribe, checkStatus, validateEmail } = useProductAvailabilityNotification();
const { data: customer } = useCustomer();
const { send } = useNotifications();

// State
const isOpen = ref(false);
const loading = ref(false);
const isSubmitting = ref(false);
const subscribed = ref(false);
const customerEmail = ref<string>('');
const acceptTerms = ref(false);
const showUnsubscribeConfirm = ref(false);
const validationErrors = reactive<Record<string, string>>({
  email: '',
  acceptTerms: '',
});

// Computed
const isAuthenticated = computed(() => !!customer.value?.email);

const buttonText = computed(() => {
  if (loading.value) return t('product_availability.button_loading');
  if (subscribed.value) return t('product_availability.button_subscribed');
  return t('product_availability.button_subscribe');
});

// Helper methods
const clearValidationErrors = () => {
  Object.keys(validationErrors).forEach((key) => {
    validationErrors[key] = '';
  });
};

const resetForm = () => {
  if (!isAuthenticated.value) customerEmail.value = '';
  acceptTerms.value = false;
  clearValidationErrors();
  showUnsubscribeConfirm.value = false;
};

const closeDialog = () => {
  isOpen.value = false;
};

const showNotification = (message: string, type: 'success' | 'error') => {
  send({ message, type });
};

// Validation
const validateForm = (): boolean => {
  clearValidationErrors();
  let isValid = true;

  if (!isAuthenticated.value) {
    if (!customerEmail.value.trim()) {
      validationErrors.email = t('product_availability.error_email_required');
      isValid = false;
    } else if (!validateEmail(customerEmail.value)) {
      validationErrors.email = t('product_availability.error_email_invalid');
      isValid = false;
    }
  }

  if (!subscribed.value && !acceptTerms.value) {
    validationErrors.acceptTerms = t('product_availability.error_terms_required');
    isValid = false;
  }

  return isValid;
};

// API methods
const checkSubscriptionStatus = async () => {
  if (!customerEmail.value) return;

  loading.value = true;
  try {
    const response = await checkStatus(
      props.productId,
      customerEmail.value,
      props.productAttributeId
    );
    subscribed.value = response.subscribed;
  } catch (err: any) {
    console.error('Error checking subscription status:', err);
  } finally {
    loading.value = false;
  }
};

const handleAction = async (
  action: typeof subscribe | typeof unsubscribe,
  successState: boolean,
  eventName: 'subscribed' | 'unsubscribed',
  errorKey: string
) => {
  isSubmitting.value = true;

  try {
    const response = await action(
      props.productId,
      customerEmail.value,
      props.productAttributeId
    );

    if (response.success) {
      subscribed.value = successState;
      if (eventName === 'subscribed') {
        emit('subscribed', customerEmail.value);
      } else {
        emit('unsubscribed', customerEmail.value);
      }
      showNotification(response.message, 'success');
      closeDialog();
    }
  } catch (err: any) {
    console.error(`Product availability ${eventName} error:`, err);
    showNotification(err.message || t(errorKey), 'error');
  } finally {
    isSubmitting.value = false;
  }
};

const handleSubmit = async () => {
  if (!validateForm()) return;
  await handleAction(subscribe, true, 'subscribed', 'product_availability.error_subscription_failed');
};

const handleUnsubscribe = async () => {
  await handleAction(unsubscribe, false, 'unsubscribed', 'product_availability.error_unsubscribe_failed');
};

// Watchers
watch(() => customer.value?.email, (email) => {
  if (email) {
    customerEmail.value = email;
    checkSubscriptionStatus();
  }
}, { immediate: true });

watch(isOpen, (newOpen) => {
  if (newOpen && customerEmail.value) {
    checkSubscriptionStatus();
  } else if (!newOpen) {
    resetForm();
  }
});

// Initialization
onMounted(() => {
  if (customer.value?.email) {
    customerEmail.value = customer.value.email;
  }
});
</script>
